// updateNotifier — tells the operator when a NEW build has shipped so they
// never sit on stale JavaScript in a long-open tab. Single-page apps do not
// hot-update: once loaded, a tab keeps running the bundle it started with until
// the page is reloaded. This polls the CRA asset-manifest (its hash changes on
// every deploy) and, when the running build no longer matches what production
// is serving, shows a small "Refresh" pill.
//
// Vanilla DOM (no React) so it survives even a render crash and adds zero
// coupling to the app tree. index.js calls startUpdateNotifier() once.

const MANIFEST = '/asset-manifest.json';
const POLL_MS = 90_000;

let baseline = null; // the main bundle this tab is actually running
let shown = false;

// The main bundle path this tab loaded with, read from the live <script> tags
// so it reflects the ACTUALLY-running build (independent of fetch timing).
function runningMain() {
  try {
    const s = Array.from(document.getElementsByTagName('script'))
      .map((x) => x.getAttribute('src') || '')
      .find((src) => /\/static\/js\/main\.[a-z0-9]+\.js/.test(src));
    if (!s) return null;
    const m = s.match(/\/static\/js\/main\.[a-z0-9]+\.js/);
    return m ? m[0] : null;
  } catch {
    return null;
  }
}

// What production is serving right now.
async function currentMain() {
  try {
    const r = await fetch(`${MANIFEST}?_=${runningMain() || 'x'}-poll`, { cache: 'no-store' });
    if (!r.ok) return null;
    const j = await r.json();
    const f = j && j.files && j.files['main.js'];
    if (!f) return null;
    const m = String(f).match(/\/static\/js\/main\.[a-z0-9]+\.js/);
    return m ? m[0] : String(f);
  } catch {
    return null;
  }
}

function showPill() {
  if (shown || typeof document === 'undefined' || !document.body) return;
  shown = true;
  const reduce = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!document.getElementById('sfa-upd-kf')) {
    const st = document.createElement('style');
    st.id = 'sfa-upd-kf';
    st.textContent = '@keyframes sfa-upd-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}';
    document.head.appendChild(st);
  }

  const wrap = document.createElement('div');
  wrap.setAttribute('role', 'status');
  wrap.style.cssText = [
    'position:fixed', 'left:20px', 'bottom:20px', 'z-index:2147483647',
    'display:flex', 'align-items:center', 'gap:12px',
    'padding:11px 12px 11px 16px', 'border-radius:14px',
    'background:linear-gradient(160deg,rgba(24,22,16,0.97),rgba(12,12,10,0.97))',
    'border:1px solid rgba(201,162,39,0.5)',
    'box-shadow:0 14px 44px rgba(0,0,0,0.55)',
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    'color:#f4efe2', 'font-size:13px', 'max-width:340px',
    reduce ? '' : 'animation:sfa-upd-in .35s ease both',
  ].join(';');

  wrap.innerHTML =
    '<span style="width:8px;height:8px;border-radius:50%;background:#C9A227;box-shadow:0 0 8px #C9A227;flex:none"></span>'
    + '<span style="line-height:1.3"><b style="font-weight:700">New version available.</b><br>'
    + '<span style="color:#c8c2b2">Refresh to get the latest.</span></span>';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = 'Refresh';
  btn.style.cssText = 'margin-left:4px;padding:7px 14px;border:0;border-radius:9px;background:linear-gradient(180deg,#E9C75B,#C9A227);color:#241c00;font-weight:800;font-size:12px;cursor:pointer;flex:none';
  btn.onclick = () => { try { window.location.reload(); } catch { /* noop */ } };

  const x = document.createElement('button');
  x.type = 'button';
  x.setAttribute('aria-label', 'Dismiss');
  x.textContent = '×';
  x.style.cssText = 'padding:2px 6px;border:0;background:transparent;color:#8b8574;font-size:18px;line-height:1;cursor:pointer;flex:none';
  // Dismiss for now; re-show on the next newly-detected build.
  x.onclick = () => { try { wrap.remove(); } catch { /* noop */ } shown = false; };

  wrap.appendChild(btn);
  wrap.appendChild(x);
  document.body.appendChild(wrap);
}

async function check() {
  const cur = await currentMain();
  if (!cur) return;
  if (baseline == null) { baseline = cur; return; }
  if (cur !== baseline) showPill();
}

export function startUpdateNotifier() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  // Baseline = the build this tab is running (from the live script tag). Fall
  // back to the first manifest fetch if the tag can't be read.
  baseline = runningMain();
  try {
    setInterval(check, POLL_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check();
    });
    window.addEventListener('focus', check);
  } catch {
    /* noop — a missing timer/listener must never break app boot */
  }
}
